import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Grid2, IconButton, Paper, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	CellKeyDownEvent,
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	Group,
	GroupImperativeHandle,
	Panel,
	Separator,
} from "react-resizable-panels";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../../components/shared/CenteredLazyLoading";
import { useContextMenuMESF } from "../../../../../controls/contextMenu";
import { themeDXT } from "../../../../../css/themeMESF";
import { Job } from "../../models/Job";
import { getJobs } from "../../repositories/JobsRepository";
import { ResetJob } from "../ResetJob";
import { OnEnableJob } from "../onEnableJob";
import { JobDetails } from "./JobsDetails";
import { useJobsOptionsFunctions } from "./customHooks/useJobsOptionsFunctions";
import { useJobsTableData } from "./customHooks/useJobsTableData";

const jobStorageKey = "job-table-panel-sizes";

const useSearchJobs = () => {
	return useQuery<Job[], Error>({
		queryKey: ["jobs"],
		queryFn: ({ signal }) => getJobs(signal),
	});
};

const CustomStatusPanel = ({ handleNew }: { handleNew: () => void }) => (
	<IconButton onClick={handleNew} color="primary" style={{ marginLeft: -15 }}>
		<PlaylistAddIcon style={{ width: 30, height: 30 }} />
	</IconButton>
);

export const TableJobs = () => {
	const queryClient = useQueryClient();
	const groupRef = useRef<GroupImperativeHandle>(null);

	const [hover, setHover] = useState(false);
	const [jobSelected, setJobSelected] = useState<Job | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [showModal, setShowModal] = useState<
		"create" | "update" | "reset" | "enable" | ""
	>("");

	const { data: rows, isLoading, isError, error: e } = useSearchJobs();

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			suppressHeaderMenuButton: true,
			flex: 1,
			cellStyle: {
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			},
		};
	}, []);

	const onRowClicked = (event: RowClickedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
			setJobSelected(event.data);
			setShowModal("update");
		}
		if (cell?.column.getColId() === "Enabled") {
			setJobSelected(event.data);
			setShowModal("enable");
		}
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { getMenuOptions } = useJobsOptionsFunctions({
		setShowModal,
		setJobSelected,
	});

	const { columnDefs } = useJobsTableData({
		setJobSelected,
		setShowModal,
		showContextMenu,
	});

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		showContextMenu(event as any, data, "jobsContext");
		if (data) {
			params.api.deselectAll();
			params.node?.setSelected(true);
		}
		return [];
	};

	const handleNewJob = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setJobSelected(null);
	};

	const onDetailsHide = async ({
		close,
		entityId,
	}: {
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setJobSelected(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		await queryClient.invalidateQueries({ queryKey: ["jobs"] }).then(() => {
			if (entityId) {
				setTimeout(() => {
					const rowNode = gridApi?.getRowNode(String(entityId));

					if (rowNode) {
						gridApi?.ensureIndexVisible(rowNode?.rowIndex ?? 0);
						rowNode?.setSelected(true);
						setJobSelected(rowNode?.data);
					}
				}, 100);
			}
		});
	};

	const onCellKeyDown = (event: CellKeyDownEvent) => {
		const { api, node, event: keyEvent } = event;
		if (!keyEvent) return;
		const key = (keyEvent as KeyboardEvent).key;

		if (key === "ArrowUp" || key === "ArrowDown") {
			keyEvent.preventDefault();
			const currentIndex = node.rowIndex !== null ? node.rowIndex : -1;
			const nextRowIndex =
				key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
			const nextNode = api.getDisplayedRowAtIndex(nextRowIndex);

			if (nextNode) {
				nextNode.setSelected(true);
				api.ensureIndexVisible(nextRowIndex);
				setJobSelected(nextNode.data);
			}
		}
	};

	const getSavedPanelLayout = (): { table: number; details: number } => {
		try {
			const saved = localStorage.getItem(jobStorageKey);
			if (saved) {
				const layout = JSON.parse(saved);
				if (layout && typeof layout === "object") {
					return layout as { table: number; details: number };
				}
			}
		} catch (e) {
			console.error("Error loading panel layout:", e);
		}
		return { table: 50, details: 50 };
	};

	const handlePanelResize = (layout: { [id: string]: number }) => {
		try {
			localStorage.setItem(jobStorageKey, JSON.stringify(layout));
		} catch (e) {
			console.error("Error saving panel layout:", e);
		}
	};

	const resetPanelLayout = () => {
		if (groupRef.current) {
			const defaultLayout = { table: 50, details: 50 };
			groupRef.current.setLayout(defaultLayout);
			try {
				localStorage.setItem(jobStorageKey, JSON.stringify(defaultLayout));
			} catch (e) {
				console.error("Error saving default layout:", e);
			}
		}
	};

	const handleSeparatorDoubleClick = () => {
		resetPanelLayout();
	};

	useEffect(() => {
		if (e && isError) {
			setErrorMessage(e.message);
		}
	}, [e, isError]);

	useEffect(() => {
		registerConfig({
			id: "jobsContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						System Jobs
					</Typography>
				</Grid2>
				<Grid2 sx={{ width: "100%" }}>
					<Group
						orientation="horizontal"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={20}>
							<Paper elevation={2} style={{ height: "70vh", width: "100%" }}>
								<AgGridReact
									loading={isLoading}
									rowData={rows}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									getContextMenuItems={getContextMenuItems}
									loadingOverlayComponent={CenteredLazyLoading}
									rowHeight={34}
									headerHeight={34}
									animateRows
									rowSelection="single"
									onGridReady={(params) => setGridApi(params.api)}
									getRowId={(params) => `${(params.data as Job).JobId}`}
									onRowClicked={onRowClicked}
									onCellKeyDown={onCellKeyDown}
									gridOptions={{
										theme: themeDXT,
										statusBar: {
											statusPanels: [
												{
													statusPanel: (params: IStatusPanelParams) => (
														<CustomStatusPanel handleNew={handleNewJob} />
													),
													align: "left",
												},
												{
													statusPanel: "agTotalRowCountComponent",
													align: "right",
												},
											],
										},
									}}
								/>
							</Paper>
						</Panel>
						<Separator
							onDoubleClick={handleSeparatorDoubleClick}
							onMouseEnter={() => setHover(true)}
							onMouseLeave={() => setHover(false)}
							style={{
								width: hover ? "10px" : "8px",
								background: hover ? "#bdbdbd" : "#e0e0e0",
								position: "relative",
								cursor: "col-resize",
								marginLeft: 5,
								marginRight: 5,
								borderRadius: 10,
							}}
						>
							<div
								style={{
									position: "absolute",
									top: "50%",
									left: "50%",
									transform: "translate(-50%, -50%)",
									width: "3px",
									height: "40px",
									background: hover ? "#616161" : "#9e9e9e",
									borderRadius: "2px",
								}}
							/>
						</Separator>
						<Panel id="details" minSize={20}>
							<Paper elevation={2} style={{ height: "70vh", width: "100%" }}>
								<JobDetails
									job={jobSelected}
									modal={showModal}
									onHide={onDetailsHide}
									suffixTitle="System Jobs"
								/>
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<ResetJob
				show={showModal === "reset"}
				onHide={async (shouldUpdate: boolean) => {
					setShowModal("");
					setJobSelected(null);
					if (gridApi) {
						gridApi.deselectAll();
					}
					if (shouldUpdate) {
						await queryClient.invalidateQueries({ queryKey: ["jobs"] });
					}
				}}
				jobSelected={jobSelected}
			/>
			<OnEnableJob
				show={showModal === "enable"}
				onHide={async (shouldUpdate: boolean) => {
					setShowModal("");
					setJobSelected(null);
					if (gridApi) {
						gridApi.deselectAll();
					}
					if (shouldUpdate) {
						await queryClient.invalidateQueries({ queryKey: ["jobs"] });
					}
				}}
				jobSelected={jobSelected}
			/>
			<ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
		</>
	);
};
